import { useEffect, useRef, useState } from 'react'

/**
 * Live (display-only) transcript via the Web Speech API.
 *
 * Runs alongside MediaRecorder while the user is holding the voice button,
 * surfacing words as they're spoken so the experience stops feeling like a
 * black hole. The transcript here is NEVER sent to the server — when
 * recording stops, the canonical transcript is the Groq Whisper result on
 * the full WebM blob. WER on names/brands/serials is unaffected.
 *
 * Browser support:
 *   - Chrome desktop / Chrome Android / Edge: webkitSpeechRecognition (works)
 *   - Safari 14.1+: SpeechRecognition (works, server-backed, audio leaves device)
 *   - Firefox: not supported — `supported=false`, caller should fall back to a
 *     "Recording…" pulse
 *
 * Errors are swallowed silently — this is feedback UX, not a critical path.
 * Network drops, permission edge cases, etc. just stop updating the interim
 * text; the user sees whatever was captured up to that point and the Groq
 * transcript handles correctness.
 */

interface UseLiveTranscriptResult {
  interim: string
  supported: boolean
}

export function useLiveTranscript(active: boolean): UseLiveTranscriptResult {
  const [interim, setInterim] = useState('')
  const recognitionRef = useRef<unknown>(null)

  // Resolve the constructor once; stable across renders.
  const SR =
    typeof window !== 'undefined'
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      : null
  const supported = !!SR

  useEffect(() => {
    if (!active || !supported) {
      // Reset on stop so the next session starts blank instead of flashing
      // the previous utterance.
      setInterim('')
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r: any = new SR()
    r.continuous = true
    r.interimResults = true
    r.lang = 'en-US'

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onresult = (e: any) => {
      let text = ''
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i][0].transcript
      }
      setInterim(text)
    }

    r.onerror = () => {
      // Silent — display-only.
    }

    try {
      r.start()
      recognitionRef.current = r
    } catch {
      // Already started (rapid toggle) or denied — leave interim empty.
    }

    return () => {
      try {
        r.stop()
      } catch {
        /* no-op */
      }
      recognitionRef.current = null
    }
  }, [active, supported, SR])

  return { interim, supported }
}
